// src/oura/newscan/fixtures/alternatives.ts
//
// "Try another" for one question: deterministic, no spend. Up to three, in
// this order:
//   ingredient  the same sentence with another pick for one of its words,
//               for this question only ("v:<variable>:<value>")
//   template    another sentence from the same group, with the row's own
//               outlet or town ("t:<template id>")
//   search      brand rows (v4.5): another pulled Google search of the same
//               group and market, asked as written ("d:<market>:<search>")
// Never one with a blank word, never one too close to any of the other 49.
// None is a valid answer: the screen then offers a reword.

import type { ScanDraftAlternative } from "@service/aeo/scanDraftTypes";
import type { AssembledRow } from "./model";
import {
    CITY_SLOTS,
    HOME_CITY_SWAPS,
    HOME_SWAPS,
    LOCAL_ANGLES,
    PLACE_VARS,
    SINGLE_EXTRAS,
    templateVars,
    type TemplateDef,
} from "./recipe";
import { renderTemplate } from "./render";
import { tooSimilar } from "./lint";
import type { WordSource } from "./words";

const MAX_ALTERNATIVES = 3;

export interface AltInput {
    row: AssembledRow;
    rows: AssembledRow[];
    src: WordSource;
    /** We go to them: the home-visit wording of the address angles. */
    home: boolean;
    /** The template the row is asked with now (its "try another" pick, an old wording, or the recipe's). */
    currentTemplate: string;
    currentTemplateId: string | null;
    /** The row's own "try another" values (variable -> value). */
    rowValues: Record<string, string>;
    /** Brand rows: other pulled searches of the same group and market, with the question each is asked as. */
    searches?: Array<{ id: string; text: string; suggestion: string }>;
}

/** The context a row's template renders with: location rows may borrow a city template. */
export function rowCtx(row: AssembledRow): Record<string, string> {
    const { plan } = row;
    return plan.check === "location" && plan.place ? { city: plan.place, ...plan.ctx } : plan.ctx;
}

/**
 * The other templates a row may be asked with: the same group, never an angle
 * its outlet already asks. English rows only. Also the pool the generation-time
 * dedupe pass (dedupe.ts) draws from.
 */
export function templatePool(row: AssembledRow, rows: AssembledRow[], home: boolean): TemplateDef[] {
    const { plan } = row;
    if (plan.language !== "en" || plan.brand) return [];
    const at = plan.key.split("@")[1];
    if (at) {
        const used = new Set(rows.filter((r) => r.plan.place === at).map((r) => r.plan.key.split("@")[0]));
        return LOCAL_ANGLES.filter((t) => !used.has(t.id)).map((t) => (home && HOME_SWAPS[t.id] ? HOME_SWAPS[t.id] : t));
    }
    const city = [...CITY_SLOTS, ...SINGLE_EXTRAS]
        .filter((t) => t.group === plan.group)
        .map((t) => (home && HOME_CITY_SWAPS[t.id] ? HOME_CITY_SWAPS[t.id] : t));
    return city;
}

export function alternativesFor(input: AltInput): ScanDraftAlternative[] {
    const { row, rows, src } = input;
    const q = row.question;
    if (q.reworded || q.locked) return [];
    const others = rows.filter((r) => r !== row).map((r) => r.question.text);
    const ctx = rowCtx(row);
    const word = (v: string) => input.rowValues[v] ?? src.value(v);
    const out: ScanDraftAlternative[] = [];
    const fits = (text: string) =>
        text.toLowerCase() !== q.text.toLowerCase() && !others.some((o) => tooSimilar(o, text)) && !out.some((a) => tooSimilar(a.text, text));

    for (const variable of templateVars(input.currentTemplate)) {
        if (PLACE_VARS.has(variable) || ctx[variable] !== undefined) continue;
        const current = word(variable);
        const picks = [src.value(variable), ...src.alternatives(variable)].filter((v) => v && v.toLowerCase() !== current.toLowerCase());
        for (const value of picks) {
            if (out.length >= MAX_ALTERNATIVES) return out;
            const r = renderTemplate(input.currentTemplate, ctx, (v) => (v === variable ? value : word(v)));
            if (r.missing.length || !fits(r.text)) continue;
            out.push({ id: `v:${variable}:${value}`, kind: "ingredient", text: r.text, why: `Uses ${value} instead of ${current || "a blank"}` });
        }
    }
    for (const template of templatePool(row, rows, input.home)) {
        if (out.length >= MAX_ALTERNATIVES) break;
        if (template.id === input.currentTemplateId) continue;
        const r = renderTemplate(template.text, ctx, (v) => src.value(v));
        if (r.missing.length || !fits(r.text)) continue;
        out.push({ id: `t:${template.id}`, kind: "template", text: r.text, why: "Asks the same kind of question another way" });
    }
    for (const s of input.searches ?? []) {
        if (out.length >= MAX_ALTERNATIVES) break;
        if (s.id === input.currentTemplateId || !fits(s.text)) continue;
        out.push({ id: s.id, kind: "template", text: s.text, why: `From Google: “${s.suggestion}”` });
    }
    return out;
}
